// Special roles (spec GP-17): a layer on the classic rules, dealt per park from the config's quotas.
// Engine words here; the screen says Mechanic (scientist), Conductor (engineer), Spotter (tracker),
// Hailer (noisemaker), Area boy (guardian), Copy-copy (shapeshifter) and Shadow (phantom).
//
// Every role shares one ability state (cooldownUntil, activeUntil, charges, target), driven by the
// 'ability.use' command and checked here. Deadlines are absolute in state.now; whether a use is
// running is always `activeUntil > now`, never a counter. With no role on, none of this exists:
// no state, no RNG draw, and the rule set is the plain one (rules.ts rulesFor), so a classic park
// hashes exactly as it did before roles.
import { isAgberoRole, rolesOn, SPECIAL_ROLES } from '@wahala/config';
import { TILE } from '@wahala/maps';
import { dist2 } from './geometry.js';
import { nextInt } from './rng.js';
import { exitVent } from './vents.js';
import { visibleTo } from './views.js';
/** Each role's levers when the park's config leaves them out. */
export const ROLE_DEFAULTS = {
    // Mechanic: the engine board (who is still running) for a while
    scientist: { cooldownMs: 15_000, durationMs: 10_000, charges: null },
    // Conductor: a few rides through the shortcuts, pushed out after a while in one
    engineer: { cooldownMs: 10_000, durationMs: 8_000, charges: 3 },
    // Spotter: marks a keke in sight and sees it on the map for a while
    tracker: { cooldownMs: 20_000, durationMs: 15_000, charges: null },
    // Hailer: no button; when knocked, the whole park hears where, for this long
    noisemaker: { cooldownMs: 0, durationMs: 6_000, charges: null },
    // Area boy: from the spirit side, shields a living keke near them from a knock
    guardian: { cooldownMs: 30_000, durationMs: 10_000, charges: null },
    // Copy-copy: wears another keke's colour, plate and name
    shapeshifter: { cooldownMs: 20_000, durationMs: 15_000, charges: null },
    // Shadow: unseen by the drivers for a while, and cannot knock while unseen
    phantom: { cooldownMs: 20_000, durationMs: 10_000, charges: null },
};
/** How near an area boy must be to the keke they shield. */
export const SHIELD_RANGE = 3 * TILE;
/** Roles with a button of their own (the conductor uses the shortcut disc; the hailer has none). */
export const ROLES_WITH_ABILITY = [
    'scientist',
    'tracker',
    'guardian',
    'shapeshifter',
    'phantom',
];
/** Roles whose ability names a keke. */
export const ROLES_WITH_TARGET = ['tracker', 'guardian', 'shapeshifter'];
/** A role's levers in this park: the config's, else the role's defaults. */
export function lever(s, role) {
    const slot = s.config.roles?.[role];
    const d = ROLE_DEFAULTS[role];
    return {
        count: slot?.count ?? 0,
        chance: slot?.chance ?? 0,
        cooldownMs: slot?.cooldownMs ?? d.cooldownMs,
        durationMs: slot?.durationMs ?? d.durationMs,
        charges: slot?.charges ?? d.charges,
    };
}
/**
 * Deal the special roles, after the agbero draw. For each role in a fixed order, each of its seats
 * is dealt with the role's chance (a seeded roll unless the chance is 100) to a seeded pick of the
 * right side's seats that have no role yet. Draws nothing at all when no role is on.
 */
export function dealRoles(s) {
    const cfg = s.config.roles;
    if (!cfg || !rolesOn(s.config))
        return;
    const special = {};
    const ability = {};
    const disguises = {};
    for (const role of SPECIAL_ROLES) {
        const l = lever(s, role);
        if (l.count === 0 || l.chance === 0)
            continue;
        const side = isAgberoRole(role) ? 'impostor' : 'crew';
        for (let i = 0; i < l.count; i++) {
            const pool = s.order.filter((id) => s.players[id]?.role === side && !Object.hasOwn(special, id));
            if (pool.length === 0)
                break;
            if (l.chance < 100 && nextInt(s.rng, 100) >= l.chance)
                continue;
            const id = pool[nextInt(s.rng, pool.length)];
            special[id] = role;
            if (role === 'shapeshifter')
                disguises[id] = `c.${Object.keys(disguises).length + 1}`;
            ability[id] = { cooldownUntil: s.now + l.cooldownMs, activeUntil: 0, charges: l.charges, target: null };
        }
    }
    s.roles = { special, ability, alarms: [], disguises };
}
export const specialOf = (s, id) => s.roles && Object.hasOwn(s.roles.special, id) ? s.roles.special[id] : null;
export const abilityOf = (s, id) => s.roles && Object.hasOwn(s.roles.ability, id) ? s.roles.ability[id] : null;
/** Is this player's ability running now (as their role counts it)? */
function running(s, p, role) {
    if (s.phase !== 'PLAYING' || specialOf(s, p.id) !== role)
        return false;
    const a = abilityOf(s, p.id);
    if (!a || a.activeUntil <= s.now)
        return false;
    // the area boy works from the spirit side; everyone else only while on the road
    return role === 'guardian' ? !p.alive && !p.departed : p.alive && !p.departed;
}
/** A shadow, unseen by the drivers right now. */
export const shadowed = (s, p) => running(s, p, 'phantom');
/** Whom this copy-copy is dressed as right now, or null. */
export function copying(s, p) {
    if (!running(s, p, 'shapeshifter'))
        return null;
    const t = abilityOf(s, p.id)?.target;
    return t && Object.hasOwn(s.players, t) ? s.players[t] : null;
}
/** The keke behind this spotter's mark right now (a disguise mark means the copy-copy), or null. */
export function markedKeke(s, p) {
    const id = marked(s, p);
    return id ? (ownerOf(s, id) ?? null) : null;
}
/**
 * The id a copy-copy's disguise goes by in the views of those who may not know who it is: c.1,
 * c.2... in deal order, fixed for the round. Never a seat id (seat ids have no '.'), and never
 * derived from the seat, the seed or anything a phone sees, so it cannot be traced back or used to
 * reconstruct the match's RNG.
 */
export function disguiseId(s, id) {
    return (s.roles && Object.hasOwn(s.roles.disguises, id) ? s.roles.disguises[id] : undefined) ?? 'c.0';
}
/** The player behind an id: a seat id as it is, or a copy-copy's disguise id as that copy-copy. */
export function ownerOf(s, id) {
    if (Object.hasOwn(s.players, id))
        return s.players[id];
    if (!s.roles || !id.startsWith('c.'))
        return undefined;
    for (const pid of s.order)
        if (specialOf(s, pid) === 'shapeshifter' && disguiseId(s, pid) === id)
            return s.players[pid];
    return undefined;
}
/**
 * May this viewer know another player's special role and what it is doing (a copy, a shadow)? The
 * end screen, spirits who see roles, and an agbero about a fellow agbero.
 */
export function mayKnow(s, viewer, other) {
    if (s.phase === 'ENDED')
        return true;
    if (!viewer.alive && s.config.ghostsSeeRoles)
        return true;
    return viewer.role === 'impostor' && other.role === 'impostor';
}
/**
 * Is this keke in this player's sight as they know it? A copy-copy in disguise is in sight only by
 * its disguise id for those who may not know who it is; by its seat id it is out of sight.
 */
function inSight(s, by, p, byDisguise) {
    if (copying(s, p) && !mayKnow(s, by, p) && !byDisguise)
        return false;
    return visibleTo(s, by.id).has(p.id);
}
/**
 * The player a command's target id means for this player: a seat id as it is, or a disguise this
 * player can see right now, as the copy-copy behind it. Anything else: undefined.
 */
function resolveTarget(s, by, id) {
    if (Object.hasOwn(s.players, id))
        return s.players[id];
    const p = ownerOf(s, id);
    return p && copying(s, p) && visibleTo(s, by.id).has(p.id) ? p : undefined;
}
/** The keke this spotter has marked right now, or null. */
export function marked(s, p) {
    return running(s, p, 'tracker') ? (abilityOf(s, p.id)?.target ?? null) : null;
}
/** Is this mechanic's engine board open right now? */
export const vitalsOpen = (s, p) => running(s, p, 'scientist');
/** The keke this area boy is shielding right now, or null. */
export function shielding(s, p) {
    return running(s, p, 'guardian') ? (abilityOf(s, p.id)?.target ?? null) : null;
}
/** Is any area boy shielding this keke right now? */
export function shielded(s, targetId) {
    if (!s.roles)
        return false;
    return s.order.some((id) => shielding(s, s.players[id]) === targetId);
}
/** What a shadow cannot do while unseen. */
const NOT_WHILE_HIDDEN = new Set([
    'player.kill',
    'player.report',
    'player.button',
    'vent.enter',
    'task.interact',
    'task.complete',
    'sabotage.fix',
]);
export const hiddenRefusal = (s, p, t) => NOT_WHILE_HIDDEN.has(t) && shadowed(s, p) ? 'HIDDEN' : null;
/** Can this player use their ability now, on this target? */
export function checkAbility(s, p, targetId) {
    const role = specialOf(s, p.id);
    const a = abilityOf(s, p.id);
    if (!role || !a || !ROLES_WITH_ABILITY.includes(role))
        return 'NO_ABILITY';
    if (s.phase !== 'PLAYING')
        return 'WRONG_PHASE';
    if (role === 'guardian') {
        if (p.alive)
            return 'NO_ABILITY';
    }
    else if (!p.alive)
        return 'NOT_ALIVE';
    // a shadow may step back into sight early, whatever the clock says
    if (role === 'phantom' && shadowed(s, p))
        return null;
    if (s.now < a.cooldownUntil || a.activeUntil > s.now)
        return 'COOLDOWN';
    if (a.charges !== null && a.charges <= 0)
        return 'NO_CHARGES';
    if (!ROLES_WITH_TARGET.includes(role))
        return targetId === undefined ? null : 'BAD_TARGET';
    if (targetId === undefined)
        return 'BAD_TARGET';
    // a spotter may mark a copy-copy by the disguise in sight
    const t = resolveTarget(s, p, targetId);
    if (!t || t.id === p.id)
        return 'BAD_TARGET';
    if (t.departed)
        return 'BAD_TARGET';
    // a copy-copy may dress as anyone still in the round: refusing a knocked keke would tell a
    // living agbero of a death nobody has found
    if (!t.alive && role !== 'shapeshifter')
        return 'BAD_TARGET';
    // the spotter marks a keke they can see right now; the area boy shields one close by. A disguised
    // copy-copy named by its seat id is out of sight for anyone who may not know who it is
    const byDisguise = t.id !== targetId;
    if (role === 'tracker' && !inSight(s, p, t, byDisguise))
        return 'OUT_OF_RANGE';
    if (role === 'guardian') {
        if (copying(s, t) && !mayKnow(s, p, t) && !byDisguise)
            return 'OUT_OF_RANGE';
        if (dist2(p.pos, t.pos) > SHIELD_RANGE * SHIELD_RANGE)
            return 'OUT_OF_RANGE';
    }
    return null;
}
export function useAbility(s, p, targetId, events) {
    const role = specialOf(s, p.id);
    const a = abilityOf(s, p.id);
    const l = lever(s, role);
    if (role === 'phantom' && shadowed(s, p)) {
        // back into sight early: the cooldown runs from now
        a.activeUntil = s.now;
        a.cooldownUntil = s.now + l.cooldownMs;
        events.push({ t: 'ability.used', playerId: p.id, role, active: false });
        return;
    }
    a.activeUntil = s.now + l.durationMs;
    a.cooldownUntil = a.activeUntil + l.cooldownMs;
    if (a.charges !== null)
        a.charges--;
    // the spotter's mark keeps the id it was marked by (a disguise stays a disguise on their map);
    // the shield and the copy name the real keke
    const t = targetId === undefined ? undefined : resolveTarget(s, p, targetId);
    a.target = !ROLES_WITH_TARGET.includes(role) || !t ? null : role === 'tracker' ? targetId : t.id;
    events.push({ t: 'ability.used', playerId: p.id, role, active: true });
}
/** The conductor rides the shortcuts: while they have rides left (the classic check runs first). */
export function conductorMayVent(s, p, action) {
    if (specialOf(s, p.id) !== 'engineer' || p.role !== 'crew')
        return 'NOT_IMPOSTOR';
    if (action !== 'enter')
        return null;
    const a = abilityOf(s, p.id);
    return a && (a.charges === null || a.charges > 0) ? null : 'NO_CHARGES';
}
/** A conductor going into a shortcut spends a ride, and the shortcut pushes them out after a while. */
export function conductorEnters(s, p) {
    if (specialOf(s, p.id) !== 'engineer' || p.role !== 'crew')
        return;
    const a = abilityOf(s, p.id);
    if (a.charges !== null)
        a.charges = Math.max(0, a.charges - 1);
    a.activeUntil = s.now + lever(s, 'engineer').durationMs;
}
/** Each tick on the road: a conductor whose ride is up climbs out; old alarms fall silent. */
export function tickRoles(s, events) {
    const r = s.roles;
    if (!r)
        return;
    for (const id of s.order) {
        const p = s.players[id];
        if (p.vent !== null && p.role === 'crew' && specialOf(s, id) === 'engineer') {
            if (s.now >= (abilityOf(s, id)?.activeUntil ?? 0))
                exitVent(s, p, events);
        }
    }
    if (r.alarms.some((x) => x.until <= s.now))
        r.alarms = r.alarms.filter((x) => x.until > s.now);
}
/** A knocked hailer is heard by the whole park, at the spot (never who or by whom). */
export function onKnocked(s, target, events) {
    if (!s.roles || specialOf(s, target.id) !== 'noisemaker')
        return;
    const until = s.now + lever(s, 'noisemaker').durationMs;
    s.roles.alarms.push({ pos: { ...target.pos }, until });
    events.push({ t: 'role.alarm', pos: { ...target.pos }, until });
}
/** A meeting ends every running ability and silences the alarms. */
export function stopRoles(s) {
    const r = s.roles;
    if (!r)
        return;
    for (const id of s.order) {
        const a = abilityOf(s, id);
        if (!a)
            continue;
        a.activeUntil = 0;
        a.target = null;
    }
    r.alarms = [];
}
/** Back on the road after a meeting: every ability starts its cooldown again. */
export function resumeRoles(s) {
    if (!s.roles)
        return;
    for (const id of s.order) {
        const role = specialOf(s, id);
        const a = abilityOf(s, id);
        if (role && a && ROLES_WITH_ABILITY.includes(role))
            a.cooldownUntil = s.now + lever(s, role).cooldownMs;
    }
}
//# sourceMappingURL=roles.js.map